/* eslint-disable */
import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import styled from 'styled-components';
import useTheme from 'theme/useTheme';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import {
  Button, Label, Dropdown, Popup, Modal, Loader, Checkbox,
} from 'semantic-ui-react';
import { ChallengeFollowChip } from 'components/lib/Chips';
import { RoundButton, Sticky } from 'components/lib/UI';
import api from 'api';
import util from 'utils/utils';
import actions from 'actions';
import toast from 'react-hot-toast';

import AdjustableUserList from './AdjustableUserList';
import ChallengePreview from 'components/lib/ChallengePreview';
import ChallengeStageChanger from './ChallengeStageChanger';

const StyledStatusBar = styled.div`
  height: ${({ theme }) => (!theme.sizes.isComputer ? 50 : 60)}px;
  padding: ${({ theme }) => (!theme.sizes.isComputer ? '10px 10px' : '10px 20px')};
  vertical-align: middle;
  background-image: ${(props) => `linear-gradient(to bottom right, ${props.theme.secondaryColour}, ${util.adjustColour(props.theme.secondaryColour, 0.5)})`};
  top: 50px;
  width:100%;
  z-index: 90;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  > div {
    display: flex;
    align-items: center;
    .ui.button {
      margin-left: 3px;
    }
  }
`;

const ChallengeStatusBar = ({
  challenge = { stage: 'published' }, challengeId, completed: propCompleted, update, t, user, loading, newIdea, myOwnIdeas, isPreview, overviewPage, redirectToLogin,
}) => {
  const [previewId, setPreviewId] = useState('');
  const [template, setTemplate] = useState({});
  const [requestToOpenChallengeOpen, setRequestToOpenChallengeOpen] = useState(false);
  const [selectedRequestAdmin, setSelectedRequestAdmin] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [contactingAdmin, setContactingAdmin] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const theme = useTheme();

  const params = new URLSearchParams(window.location.search);
  const shouldOpenChannel = params.get('shouldOpenChallenge', false);

  const getChallenge = useCallback(() => {
    api.announcements.get('challenge', challengeId, null, (data) => setAnnouncements(data.automaticAnnouncements));
    api.challenges.getTemplate(challengeId, (newTemplate) => setTemplate(newTemplate));
  }, [challengeId]);

  useEffect(() => getChallenge(), [getChallenge]);

  const userOrg = user?.organisation._id;
  const getAdmins = useCallback(() => {
    if (userOrg) {
      setAdminsLoading(true);
      api.organisations.getAdmins(userOrg, { limit: 10, roles: ['org.publishChallenges'] }, ({ users }) => {
        setAdmins(users);
        setAdminsLoading(false);
      }, (err) => {
        toast.error(err.message);
        setAdminsLoading(false);
      });
    }
  }, [userOrg]);

  const contactAdmin = useCallback(() => {
    setContactingAdmin(true);
    api.organisations.contactAdmin(userOrg, { challenge: challengeId, contactType: 'requestOpenChallenge', admin: selectedRequestAdmin }, () => {
      toast.success('Request sent to admin');
      setSelectedRequestAdmin('');
      setContactingAdmin(false);
      setRequestToOpenChallengeOpen(false);
      setRequestSent(true);
    }, (err) => {
      toast.error(err.message);
      setContactingAdmin(false);
    })
  }, [selectedRequestAdmin, userOrg, challengeId]);

  useEffect(() => {
    if (requestToOpenChallengeOpen) {
      getAdmins();
    }
  }, [requestToOpenChallengeOpen, getAdmins]);

  const updateStage = useCallback((newStage, isSoftOpen = false) => {
    const completed = { ...(propCompleted || {}) };
    if (!propCompleted) {
      completed.audienceCompleted = challenge.audienceSize > 0 ? 'done' : false;
      completed.templateCompleted = template.body?.length > 0 ? 'done' : false;

      const isDone = !!(challenge?.description?.length > 0 && challenge?.description !== undefined && challenge?.instructions?.length > 0 && challenge?.instructions !== undefined && challenge.bannerImage && challenge.name);

      const isAlmost = challenge.description || challenge.instructions || challenge.sector || challenge.video || challenge.coverImage || challenge.bannerImage;

      completed.detailsCompleted = (isDone ? 'done' : (isAlmost ? 'almost' : false));
      completed.announcementCompleted = announcements?.length > 0 ? 'done' : false;
    }
    const updateStageFunction = () => api.challenges.updateStage(challengeId, newStage, isSoftOpen, (s) => update(challengeId, s));
    if (!isSoftOpen && newStage === 'published' && (completed.audienceCompleted !== 'done' || completed.templateCompleted !== 'done' || completed.announcementCompleted !== 'done' || completed.detailsCompleted !== 'done')) {
      util.confirm('Really open this challenge?', 'You have not completed all the recommened sections of the challenge, are you sure you want to open the challenge.').then(() => {
        updateStageFunction();
      }, () => { }).catch(() => { });
    } else updateStageFunction();
  }, [announcements, template, challenge, challengeId, propCompleted, update]);

  const updateFollowerCount = useCallback((increase = true) => {
    let { followerCount = 0 } = challenge;
    if (increase) followerCount += 1;
    else followerCount -= 1;
    update(challenge._id, { followerCount });
  }, [challenge, update]);

  const pinChallenge = useCallback(() => {
    const { isPinned, _id } = challenge;
    api.challenges.setStatus(_id, { isPinned: !isPinned }, () => update(_id, { isPinned: !isPinned }));
  }, [update, challenge]);

  const challengeStage = challenge.stage;
  useEffect(() => {
    if (shouldOpenChannel && challengeStage !== 'published') {
      updateStage('published');
    }
  }, [shouldOpenChannel, challengeStage, updateStage]);

  const canPublishChallenges = useMemo(() => util.isInRole(user, 'org.publishChallenges', user?.organisation?._id), [user]);
  const canMakeIdea = util.canCreateIdea(user, challenge);
  const myIdeas = useMemo(() => (isPreview ? [] : myOwnIdeas), [isPreview, myOwnIdeas]);
  const myUnsubmittedIdeas = useMemo(() => myIdeas?.filter((idea) => !idea.isSubmitted), [myIdeas]);

  const updateStageButton = useMemo(() => {
    if (challenge?.stage === 'published') {
      return (
        <RoundButton size='tiny' disabled={!canPublishChallenges} secondary content={t('challenge.stages.published.close')} color='teal' />
      );
    }
    if (canPublishChallenges) {
      if (!challenge?.stage) {
        return (
          <RoundButton size='tiny' primary content={t('challenge.stages.draft.open')} color='teal' />
        );
      }
      if (challenge?.stage === 'closed') {
        return (
          <RoundButton size='tiny' primary content={t('challenge.stages.closed.reOpen')} />
        );
      }
    }
  }, [challenge, canPublishChallenges, requestSent, t]);

  return (
    <Sticky top={50} style={{ zIndex: 90 }} >
      <StyledStatusBar theme={theme}>
        <Modal
          open={requestToOpenChallengeOpen}
          onClose={() => setRequestToOpenChallengeOpen(false)}
        >
          <Modal.Header>Request to open challenge</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <p>You do not have permission to open challenges in your organisation. Please select an admin, who will receive a request from you to open this challenge.</p>
            </Modal.Description>
            {adminsLoading
              ? (
                <Loader active />
              ) : (
                <div>
                  <AdjustableUserList
                    results={admins}
                    actions={(adminUser) => (
                      <Checkbox
                        checked={selectedRequestAdmin === adminUser._id}
                        onClick={() => {
                          setSelectedRequestAdmin((prevId) => prevId === adminUser._id ? '' : adminUser._id)
                        }}
                        size='large'
                      />
                    )}
                  />
                </div>
              )
            }
          </Modal.Content>
          <Modal.Actions>
            <Button content='Cancel' icon='close' onClick={() => setRequestToOpenChallengeOpen(false)} />
            <Button color='green' icon='check' content='Send request' disabled={!selectedRequestAdmin} loading={contactingAdmin} onClick={contactAdmin} />
          </Modal.Actions>
        </Modal>

        <div>
          {theme.sizes.isComputer
            ? (
              <>
                {!challenge.stage
                  && <Label color='yellow' style={{ marginRight: 10 }}>{t('challenge.stages.draft.title')}</Label>
                }
                {challenge.stage === 'published'
                  && <Label color='green' style={{ marginRight: 10 }}>{t('challenge.stages.published.title')}</Label>
                }
                {challenge.stage === 'closed'
                  && <Label color='orange' style={{ marginRight: 10 }}>{t('challenge.stages.closed.title')}</Label>
                }
              </>
            ) : null
          }

          {!util.canManageChallenge(user, challenge) && theme.sizes.isComputer
            && <span style={{ color: 'white', fontSize: 16 }}>{challenge.name}</span>
          }

          {theme.sizes.isComputer && util.canManageChallenge(user, challenge)
            && <React.Fragment>
              <Button size='tiny' as='div' labelPosition='left' style={{ marginRight: 10 }}>
                <Label basic>{challenge.ideaCount || 0}</Label>
                <Button size='tiny' as={Link} to={`/challenges/${challenge._id}/ideas`} icon='lightbulb' content={t('generic.ideas')} inverted basic />
              </Button>

              <Button size='tiny' as='div' labelPosition='left'>
                <Label basic>{challenge.audienceSize || 0}</Label>
                <Button size='tiny' as={Link} inverted basic to={`/challenges/${challenge._id}/settings/audience`} icon='users' content={t('challenge.settings.audience.title')} />
              </Button>

            </React.Fragment>
          }

          <ChallengeFollowChip challenge={challenge} onFollow={() => updateFollowerCount(true)} onUnfollow={() => updateFollowerCount(false)} style={{ height: 32, marginLeft: 7 }} />

          {util.isInRole(user, 'org.manageChallenges', user?.organisation?._id) && !theme.sizes.isMobile
            ? (
              <Popup
                content={'Pinned challenges will show at the top of users\' \'Challenges\' page. They will also appear on the homepage'}
                on='hover'
                trigger={(
                  <Button size='tiny' inverted basic content={challenge.isPinned ? 'Un-pin' : 'Pin'} icon='thumbtack' style={{ marginLeft: 7 }} onClick={pinChallenge} />
                )}
              />
            ) : null
          }
        </div>
        <ChallengePreview challengeId={previewId} closeModal={() => setPreviewId('')} />
        {loading
          ? <div />
          : (
            <div>
              {util.canManageChallenge(user, challenge)
                ? <>
                  {!theme?.sizes?.isMobile
                    ? <React.Fragment>
                      {window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/)
                        && <RoundButton size='tiny' content={t('challenge.settings.viewChallenge')} as={Link} to={`/challenges/${challenge._id}`} icon='arrow left' inverted basic />
                      }
                      {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/)
                        && <RoundButton size='tiny' icon='cogs' inverted basic content={t('challenge.settings.title')} as={Link} to={`/challenges/${challenge._id}/settings`} />
                      }
                      {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/format/)
                        && <RoundButton size='tiny' icon='pencil' inverted basic content={t('challenge.editFormat')} as={Link} to={`/challenges/${challenge._id}/settings/format`} />
                      }
                      {challenge.stage !== 'published' && !canPublishChallenges ? (
                        <RoundButton
                          size='tiny'
                          primary
                          disabled={requestSent}
                          icon={requestSent ? 'check' : undefined}
                          content={t('challenge.stages.draft.request' + (requestSent ? 'Sent' : ''))}
                          onClick={() => setRequestToOpenChallengeOpen(true)}
                        />
                      ) : (
                        <ChallengeStageChanger
                          challengeId={challengeId}
                          trigger={updateStageButton}
                          updateStage={updateStage}
                        />
                      )}
                    </React.Fragment>
                    : <Dropdown icon={null} direction='left'
                      trigger={<Button basic inverted icon='cogs' size='tiny' />}
                    >

                      <Dropdown.Menu>
                        {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/format/)
                          && <Dropdown.Item icon='pencil' content={t('challenge.editFormat')} as={Link} to={`/challenges/${challenge._id}/settings/format`} />
                        }
                        {window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/)
                          && <Dropdown.Item icon='arrow left' content={t('challenge.settings.viewChallenge')} as={Link} to={`/challenges/${challenge._id}`} />
                        }
                        {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/)
                          && <Dropdown.Item icon='cogs' content={t('challenge.settings.title')} as={Link} to={`/challenges/${challenge._id}/settings`} />
                        }
                        <Dropdown.Item icon='thumbtack' content={challenge.isPinned ? 'Un-pin challenge' : 'Pin challenge'} onClick={pinChallenge} />
                        {canPublishChallenges
                          ? (
                            <>
                              {!challenge.stage
                                && <Dropdown.Item content={t('challenge.stages.draft.open')} onClick={() => updateStage('published')} />
                              }
                              {challenge.stage === 'published'
                                && <Dropdown.Item content={t('challenge.stages.published.close')} onClick={() => updateStage('closed')} />
                              }
                              {challenge.stage === 'closed'
                                && <Dropdown.Item content={t('challenge.stages.closed.reOpen')} onClick={() => updateStage('published')} />
                              }
                            </>
                          ) : null
                        }
                      </Dropdown.Menu>
                    </Dropdown>
                  }

                </>
                : (!theme?.sizes?.isMobile && overviewPage && (
                  <div>
                    {challenge?.stage === 'published' && !user
                      && <>
                        {challenge.ideaVisibility === 'users'
                          && <RoundButton
                            size='tiny'
                            icon='unhide'
                            content={t('challenge.viewOtherIdeas')}
                            as={Link}
                            to={`/challenges/${challenge._id}/ideas`}
                          />
                        }

                        <RoundButton
                          size='tiny'
                          primary
                          icon='plus'
                          content={'Create your idea'}
                          onClick={canMakeIdea ? newIdea : () => redirectToLogin(undefined, undefined)}
                        />

                        {myUnsubmittedIdeas?.length === 1
                          && <RoundButton
                            size='tiny'
                            icon='lightbulb'
                            content={t('challenge.continueIdea')}
                            primary
                            as={Link}
                            to={`/ideas/${myIdeas[0]._id}`}
                          />
                        }

                        {myUnsubmittedIdeas?.length > 1
                          && <Dropdown
                            icon={null}
                            trigger={(
                              <RoundButton size='tiny' primary icon='lightbulb' content={t('challenge.continueIdea')} />
                            )}
                          >
                            <Dropdown.Menu>
                              {myIdeas.map((i) => <Dropdown.Item key={i._id} as={Link} to={`/ideas/${i._id}`} content={i.name} />)}
                            </Dropdown.Menu>
                          </Dropdown>
                        }
                      </>
                    }

                    {challenge?.stage === 'published' && user && !isPreview
                      && <>
                        {(((challenge.ideaCount >= 1 && challenge.ideaVisibility === 'users') || util.canManageChallenge(user, challenge)) && user)
                          && (
                            <RoundButton
                              size='tiny'
                              icon='unhide'
                              content={t('challenge.viewOtherIdeas')}
                              as={Link}
                              to={`/challenges/${challenge._id}/ideas`}
                            />
                          )
                        }

                        {(!myUnsubmittedIdeas || myUnsubmittedIdeas?.length === 0)
                          && (
                            <RoundButton
                              size='tiny'
                              primary
                              icon='plus'
                              content={myIdeas?.length === 0 ? t('challenge.ideas.createYourIdea') : t('challenge.startNew')}
                              onClick={canMakeIdea ? newIdea : () => redirectToLogin(undefined, undefined)}
                            />
                          )
                        }

                        {myUnsubmittedIdeas?.length === 1
                          && <RoundButton
                            size='tiny'
                            icon='lightbulb'
                            content={t('challenge.continueIdea')}
                            primary
                            as={Link}
                            to={`/ideas/${myIdeas[0]._id}`}
                          />
                        }

                        {myUnsubmittedIdeas?.length > 1
                          && <Dropdown
                            icon={null}
                            trigger={(
                              <RoundButton size='tiny' style={{ width: '100%' }} primary icon='lightbulb' content={t('challenge.continueIdea')} />
                            )}
                          >
                            <Dropdown.Menu>
                              {myIdeas.map((i, index) => <Dropdown.Item key={index} as={Link} to={`/ideas/${i._id}`} content={i.name} />)}
                            </Dropdown.Menu>
                          </Dropdown>
                        }

                      </>
                    }
                  </div>
                ))
              }
            </div>
          )
        }
      </StyledStatusBar>
    </Sticky>
  );
};

const mapDispatchToProps = (dispatch) => ({
  update: (id, challenge) => dispatch(actions.challenges.update(id, challenge)),
});
const mapStateToProps = (state, props) => ({ user: props.isPreview ? null : state.user });
const ChallengeStatusBarContainer = connect(
  mapStateToProps, mapDispatchToProps,
)(ChallengeStatusBar);

export default withTranslation()(ChallengeStatusBarContainer);
